import { BaseWebComponentElement } from './BaseWebComponentElement';
import { BaseWebComponentRepository } from './BaseWebComponentRepository';
import { BaseWebComponentView } from './BaseWebComponentView';
import { ServerResponseKind } from '../types/common/types';
import { BaseWebComponentTranslator } from './BaseWebComponentTranslator';

export interface IInitialSetup {
  viewConstructor: typeof BaseWebComponentView;
  dataRepositoryConstructor?: new () => BaseWebComponentRepository;
  viewTemplate: HTMLElement;
}

export interface WebComponentEvent<T = any> extends CustomEvent<T> {
  target: HTMLElement;
}

export type CustomEventListener = (evt: WebComponentEvent) => void;

export interface IListenerConfig {
  element: HTMLElement;
  event: string;
  callback: CustomEventListener;
}

export class BaseWebComponentController {
  public component: BaseWebComponentElement;
  public dataRepository?: BaseWebComponentRepository;
  public view: BaseWebComponentView;
  public dependenciesInjected: boolean;
  public dependencies: any;
  public viewConstructor: typeof BaseWebComponentView;
  public viewTemplate: HTMLElement;
  public dataRepositoryConstructor?: typeof BaseWebComponentRepository;
  public translator: BaseWebComponentTranslator;

  constructor(component: BaseWebComponentElement) {
    this.component = component;
  }

  computeEventListeners(): IListenerConfig[] {
    return [];
  }

  initialSetup({ viewConstructor, dataRepositoryConstructor, viewTemplate }: IInitialSetup) {
    this.dependenciesInjected = false;
    this.dependencies = {};

    this.viewConstructor = viewConstructor;
    this.viewTemplate = viewTemplate;
    this.dataRepositoryConstructor = dataRepositoryConstructor;
    this.translator = BaseWebComponentTranslator.getInstance();
  }

  injectDependencies(dependencies) {
    this.dependencies = dependencies;
    this.build();
    this.dependenciesInjected = true;
  }

  build() {
    this.buildDataRepository();
    this.buildView();
  }

  buildDataRepository() {
    if (!this.dataRepositoryConstructor) {
      return;
    }
    this.dataRepository = new this.dataRepositoryConstructor();
    this.dataRepository.injectDependencies(this.dependencies);
  }

  buildView() {
    if (!this.viewConstructor) {
      throw new Error('View constructor not defined');
    }
    if (!this.viewTemplate) {
      throw new Error('View template not defined');
    }

    let shadow: ShadowRoot;
    if (!this.component.shadowRoot) {
      shadow = this.component.attachShadow({ mode: 'open' });
    } else {
      shadow = this.component.shadowRoot;
    }
    this.view = new this.viewConstructor(shadow, this.viewTemplate);
    this.view.injectDependencies(this.dependencies);
  }

  async render() {
    try {
      const res = await this.getDataForView();
      this.view.render({
        kind: ServerResponseKind.SUCCESS,
        response: res,
      });
      return;
    } catch (err) {
      this.view.render({
        kind: ServerResponseKind.ERROR,
        error: err,
      });
    }
  }

  connected() {
    this.view.connected();
  }

  disconnected() {
    this.view.disconnected();
  }

  prepareListeners(): Array<IListenerConfig> {
    const defaultListeners = this.computeEventListeners();
    if (!defaultListeners || !Array.isArray(defaultListeners)) {
      return [];
    }

    const addedListeners: Array<IListenerConfig> = [];
    for (const listenerConfig of defaultListeners) {
      const { element, event, callback } = listenerConfig;
      if (!element || !event || !callback) {
        continue;
      }

      const formattedCallback = async ev => {
        return await callback.call(this, ev);
      };

      element.addEventListener(event, formattedCallback);
      addedListeners.push({ element, event, callback: formattedCallback });
    }

    return addedListeners;
  }

  async start() {
    this.view.start();
    this.prepareListeners();
    await this.render();
  }

  async stop() {
    this.view.stop();
  }

  async getDataForView(): Promise<any> {
    // to be implemented
    return {};
  }
}
